import type { FunctionComponent } from 'react';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

import { ctw } from '@/common';
import { ReportWrapper } from '../../report-shared/components';
import { QuestionMarkTooltip } from '../../report-shared/components/QuestionMarkTooltip';

export type BallerineNetworkInsightsStatus =
  | 'clear'
  | 'off_boarding_reported'
  | 'high_risk_off_boarding_reported';

export type BallerineNetworkInsightsData = {
  status: BallerineNetworkInsightsStatus;
  reportDate?: string;
  reason?: string;
} | null;

type BallerineNetworkInsightsSectionProps = {
  networkInsights?: BallerineNetworkInsightsData;
};

type StatusConfig = {
  label: string;
  description: string;
  icon: typeof CheckCircle2;
  iconColor: string;
  badgeBg: string;
  badgeText: string;
};

const getStatusConfig = (status: BallerineNetworkInsightsStatus): StatusConfig => {
  switch (status) {
    case 'clear':
      return {
        label: 'No Off-Boarding Reported',
        description: 'No prior off-boarding records identified across the network.',
        icon: CheckCircle2,
        iconColor: 'text-slate-400',
        badgeBg: 'bg-slate-100',
        badgeText: 'text-slate-600',
      };
    case 'off_boarding_reported':
      return {
        label: 'Off-Boarding Reported',
        description: 'A prior off-boarding record was identified.',
        icon: AlertTriangle,
        iconColor: 'text-orange-600',
        badgeBg: 'bg-orange-100',
        badgeText: 'text-orange-700',
      };
    case 'high_risk_off_boarding_reported':
      return {
        label: 'High Risk Off-Boarding Reported',
        description: 'A high-risk off-boarding record was identified.',
        icon: XCircle,
        iconColor: 'text-red-600',
        badgeBg: 'bg-red-100',
        badgeText: 'text-red-700',
      };
    default:
      return {
        label: 'No Off-Boarding Reported',
        description: 'No prior off-boarding records identified across the network.',
        icon: CheckCircle2,
        iconColor: 'text-slate-400',
        badgeBg: 'bg-slate-100',
        badgeText: 'text-slate-600',
      };
  }
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

export const BallerineNetworkInsightsSection: FunctionComponent<
  BallerineNetworkInsightsSectionProps
> = ({ networkInsights }) => {
  const effectiveInsights = networkInsights ?? { status: 'clear' as const };
  const config = getStatusConfig(effectiveInsights.status);
  const StatusIcon = config.icon;

  return (
    <ReportWrapper
      title={
        <div className="flex items-center gap-2">
          <span>Prior Off-Boarding On Ballerine's Network</span>
          <QuestionMarkTooltip text="Indicates whether the merchant has a known history of off-boarding by other acquirers in Ballerine's Network" />
        </div>
      }
      showTooltip={false}
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <StatusIcon className={ctw('h-4 w-4', config.iconColor)} />
          <span
            className={ctw(
              'inline-flex items-center rounded-md px-2.5 py-1 text-sm font-medium',
              config.badgeBg,
              config.badgeText,
            )}
          >
            {config.label}
          </span>
        </div>

        {effectiveInsights.reason && (
          <p className="text-sm text-slate-600">{effectiveInsights.reason}</p>
        )}

        {effectiveInsights.reportDate && (
          <p className="text-xs text-slate-400">
            Reported on {formatDate(effectiveInsights.reportDate)}
          </p>
        )}

        {!effectiveInsights.reason && config.description && (
          <p className="text-sm text-slate-500">{config.description}</p>
        )}
      </div>
    </ReportWrapper>
  );
};
